/* Velara predictive search — live results overlay via Predictive Search API.
   Works with the overlay's own field OR with a field that lives in the header
   (settings.search_input_in_header): every [data-search-input] on the page feeds
   [data-search-results], wherever it sits. */
(function () {
  const drawer = () => document.getElementById('VelaraSearchDrawer');
  const OVERLAY_EVENT = 'velara:overlay-open';
  const SOURCE = 'search';
  let timer = null;

  const inputs = () => [...document.querySelectorAll('[data-search-input]')];
  const visibleInput = () => inputs().find((i) => i.offsetParent !== null || i.getClientRects().length) || inputs()[0];

  function isOpen() {
    const d = drawer();
    return !!d && d.getAttribute('aria-hidden') === 'false';
  }

  /* A field can carry a second placeholder for the open state — nove25.net swaps
     "Search" → "What are you searching for?" the moment search opens. */
  function setPlaceholders(open) {
    inputs().forEach((i) => {
      const alt = i.getAttribute('data-placeholder-open');
      if (!alt) return;
      if (open) {
        if (!i.hasAttribute('data-placeholder-rest')) i.setAttribute('data-placeholder-rest', i.placeholder);
        i.placeholder = alt;
      } else {
        i.placeholder = i.getAttribute('data-placeholder-rest') || i.placeholder;
      }
    });
  }

  function open(from) {
    const d = drawer(); if (!d || isOpen()) return;
    // one overlay at a time: the site menu / account panels close themselves on this
    document.dispatchEvent(new CustomEvent(OVERLAY_EVENT, { detail: { source: SOURCE } }));
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('velara-search-open');
    setPlaceholders(true);
    // Full results: show the "before typing" collection straight away (Clerk opened on products).
    if (d.dataset.searchMode === 'full' && d.dataset.searchBrowse && window.VelaraOmni &&
        !inputs().some((i) => i.value.trim().length >= 2)) {
      const box = d.querySelector('[data-search-results]');
      if (box) window.VelaraOmni.browse(d.dataset.searchBrowse, box);
    }
    // Trap focus only when the overlay owns the field. With the field in the header the trap
    // would fight the header — the input, and the back control beside it, live outside the panel.
    const own = d.querySelector('[data-search-input]');
    if (own) {
      window.VelaraA11y && VelaraA11y.trap(d.querySelector('.velara-search-drawer__panel'));
      setTimeout(() => own.focus(), 50);
      return;
    }
    const target = (from && from.matches && from.matches('[data-search-input]')) ? from : visibleInput();
    if (target) setTimeout(() => target.focus(), 50);
  }

  function close() {
    const d = drawer(); if (!d) return;
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('velara-search-open');
    setPlaceholders(false);
    window.VelaraA11y && VelaraA11y.release();
    const results = d.querySelector('[data-search-results]');
    if (results) { results.innerHTML = ''; results.removeAttribute('aria-busy'); }
    if (window.VelaraOmni) window.VelaraOmni.reset();
    announce('');
    inputs().forEach((i) => { i.value = ''; i.blur(); });
  }

  document.addEventListener(OVERLAY_EVENT, (e) => {
    if (e.detail && e.detail.source !== SOURCE) close();
  });

  // Shopify.routes.root always ends in "/" ("/" or "/es/"), so this stays locale-correct.
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const searchUrl = (q) => `${root()}search?q=${encodeURIComponent(q)}`;

  /* Results come from the predictive-search SECTION (Section Rendering API), not from
     suggest.json: the section renders the theme's own product-card snippet, so search cards are
     the PLP cards. `seq` drops out-of-order responses — a slow "ta" must not overwrite "taro". */
  const SECTION = 'predictive-search';
  const LIMIT = 8;
  let seq = 0;

  // The one line a screen reader hears per search ("170 risultati") — see search-drawer.liquid.
  function announce(text) {
    const s = document.querySelector('[data-search-status]');
    if (s) s.textContent = text;
  }

  async function search(q) {
    const d = drawer();
    if (!d) return;
    const results = d.querySelector('[data-search-results]');
    if (!results) return;
    // Full results mode: search-omni.js owns the panel from here (same min length, same results box).
    if (q && q.length >= 2 && d.dataset.searchMode === 'full' && window.VelaraOmni) {
      ++seq; // a preview reply still in flight must not land on top of the full results
      window.VelaraOmni.search(q, results);
      return;
    }
    if (!q || q.length < 2) {
      ++seq; // a reply still in flight for the longer query must not land on the cleared panel
      // Full results with a "before typing" collection: going back under 2 characters shows it again.
      if (d.dataset.searchMode === 'full' && d.dataset.searchBrowse && window.VelaraOmni) {
        window.VelaraOmni.browse(d.dataset.searchBrowse, results);
        announce('');
        return;
      }
      if (window.VelaraOmni) window.VelaraOmni.reset();
      results.innerHTML = ''; results.removeAttribute('aria-busy'); announce('');
      return;
    }
    const mine = ++seq;
    const enc = encodeURIComponent(q);
    results.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${root()}search/suggest?q=${enc}&resources[type]=product,collection,query&resources[limit]=${LIMIT}&section_id=${SECTION}`);
      const html = await res.text();
      if (mine !== seq) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const root_el = doc.querySelector('.velara-predict');
      results.innerHTML = root_el ? root_el.outerHTML : '';
      results.removeAttribute('aria-busy');
    } catch (e) {
      if (mine === seq) { results.innerHTML = ''; results.removeAttribute('aria-busy'); }
      return;
    }
    const all = results.querySelector('.velara-predict__all');
    const reveal = () => { if (all) all.removeAttribute('data-pending'); };
    setTimeout(reveal, 1500); // a slow count must not keep the button hidden; it'll widen late instead
    // Total count for the "view all" button — predictive search doesn't return one, so it comes
    // from the count-only search view. Rendered late on purpose: the results must not wait for it.
    try {
      const c = await fetch(`${root()}search?q=${enc}&type=product&view=count`);
      const n = parseInt((await c.text()).trim(), 10);
      if (mine !== seq) return;
      if (n >= 0) {
        const slot = results.querySelector('[data-predict-count]');
        if (slot) slot.textContent = `(${n})`;
        const label = document.querySelector('[data-search-status]');
        announce(`${n} ${(label && label.dataset.label) || ''}`.trim());
      }
    } catch (e) { /* button just shows countless */ }
    if (mine === seq) reveal();
  }

  /* The search PAGE's categories row (main-search → show_collections). /search never returns
     collections, so the row is filled from the same predictive-search section the overlay uses —
     one source for what counts as a matching, visible category. Stays hidden on any failure. */
  async function fillPageCollections() {
    const box = document.querySelector('[data-search-collections]');
    const list = box && box.querySelector('[data-search-collections-list]');
    const q = box && box.dataset.terms;
    if (!list || !q) return;
    try {
      const res = await fetch(`${root()}search/suggest?q=${encodeURIComponent(q)}&resources[type]=collection&resources[limit]=10&section_id=${SECTION}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const links = [...doc.querySelectorAll('[data-predict-collections] a[href]')];
      if (!links.length) return;
      links.forEach((src) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = src.getAttribute('href');
        a.textContent = src.textContent.trim();
        li.appendChild(a);
        list.appendChild(li);
      });
      box.hidden = false;
    } catch (e) { /* no row */ }
  }
  fillPageCollections();

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-search-open]');
    if (opener) {
      // A header field is BOTH opener and input: let the click place the caret, don't preventDefault.
      if (!opener.matches('[data-search-input]')) e.preventDefault();
      open(opener);
      return;
    }
    if (e.target.closest('[data-search-close]')) { close(); }
  });
  // keyboard / programmatic focus opens too (tabbing into the header field)
  document.addEventListener('focusin', (e) => {
    const opener = e.target.closest && e.target.closest('[data-search-open]');
    if (opener) open(opener);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
    // A header field is not inside a <form>; Enter must still run the full search.
    if (e.key === 'Enter' && e.target.matches && e.target.matches('[data-search-input]') && !e.target.form) {
      const q = e.target.value.trim();
      if (q) { e.preventDefault(); window.location.href = searchUrl(q); }
    }
  });
  document.addEventListener('input', (e) => {
    const input = e.target.closest('[data-search-input]');
    if (!input) return;
    if (!isOpen()) open(input);
    clearTimeout(timer);
    timer = setTimeout(() => search(input.value.trim()), 200);
  });

  window.VelaraSearch = { open, close, announce };
})();
